"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Eye, Heart, Sparkles, TrendingUp, Users } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ContentModal from "../components/ContentModal";
import ContentCard from "../components/dashboard/ContentCard";
import TrendingCreators from "../components/dashboard/TrendingCreators";
import MostUsedEffects from "../components/discover/MostUsedEffects";
import RecentEffects from "../components/discover/RecentEffects";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import {
  getDiscoverContent,
  selectDiscoverContent,
  selectDiscoverHasMore,
  selectDiscoverLoading,
} from "../store/discoverSlice";
import { toggleLikeContent } from "../store/dreamworldSlice";
import { AppDispatch, RootState } from "../store/store";
import type { LibraryItem } from "../types/library";
import Observer from "../utils/Observer";
import { Effect } from "../types/effects";
import EffectDescriptionPanel from "../components/create/EffectDescriptionPanel";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/ui/PageLayout";
import Section from "../components/ui/Section";

const DiscoveryPage: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedContent, setSelectedContent] = useState<LibraryItem | null>(
    null
  );
  const [selectedEffect, setSelectedEffect] = useState<Effect | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );
  const content = useSelector(selectDiscoverContent);
  const hasMore = useSelector(selectDiscoverHasMore);
  const loading = useSelector(selectDiscoverLoading);
  const lastVisible = useSelector(
    (state: RootState) => state.discover.lastVisible
  );

  const loadMoreContent = useCallback(async () => {
    if (loading === "pending" || !hasMore) return;
    dispatch(getDiscoverContent(lastVisible || undefined));
  }, [hasMore, lastVisible, loading]);

  useEffect(() => {
    if (content.length === 0) {
      loadMoreContent();
    }
  }, []);

  const handleLike = (id: string) => {
    if (userGlobalData) {
      dispatch(
        toggleLikeContent({ contentId: id, userId: userGlobalData.user.uid })
      );
    }
  };

  const trackListEnd = (elementIndex: number) => {
    if (elementIndex === content.length - 1) {
      loadMoreContent();
    }
  };

  const handleEffectClick = (effect: Effect) => {
    setSelectedEffect(effect);
  };

  const handleStartEditing = (
    effect: Effect,
    images: File[],
    userInput?: string
  ) => {
    navigate("/create", {
      state: {
        effect,
        images,
        userInput,
        activeGenerator: effect.type,
      },
    });
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900">
        {/* Hero Section - Mobile Optimized */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="relative overflow-hidden bg-gradient-to-r from-purple-900/20 to-pink-900/20 border-b border-purple-500/20"
        >
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 p-2">
              <div>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-left justify-left space-x-2 mb-2 sm:mb-2"
                >
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-purple-400" />
                  <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                    Discover
                  </h1>
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-pink-400" />
                </motion.div>
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-sm sm:text-lg lg:text-xl text-gray-300 mb-2 sm:mb-2 max-w-2xl mx-auto "
                >
                  Explore breathtaking AI-generated content from our creative
                  community
                </motion.p>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="grid  grid-cols-3 gap-4 sm:flex sm:items-left sm:justify-left sm:space-x-8 text-xs sm:text-sm text-gray-400 max-w-md sm:max-w-none mx-auto"
                >
                  <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2">
                    <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-center sm:text-left">
                      12.5K Creators
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2">
                    <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-center sm:text-left">2.1M Views</span>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center space-y-1 sm:space-y-0 sm:space-x-2">
                    <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-center sm:text-left">450K Likes</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.section>
        <PageLayout>
          <motion.section
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              delay: 0.2,
              duration: 0.3,
              type: "spring",
              stiffness: 300,
              damping: 20,
            }}
          >
          <Section
            title="Trending Creators"
            onSeeAll={() => navigate("/creators")}
          >
              <TrendingCreators />
          </Section>
          </motion.section>
          <MostUsedEffects onEffectClick={handleEffectClick} />
          <RecentEffects onEffectClick={handleEffectClick} />
          {/* Mobile-Optimized Content Grid */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
          <Section title="Latest Creations">
            {/* Mobile-First Masonry Layout */}
            {true && (
              <div className="columns-2 sm:columns-2 lg:columns-3 xl:columns-4 gap-0.5 sm:gap-1 lg:gap-1 space-y-3 sm:space-y-4 lg:space-y-6">
                <AnimatePresence>
                  {content.map((item, index) => (
                    <Observer
                      key={item.id}
                      onVisible={trackListEnd}
                      index={index}
                    >
                      <motion.div
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        className="break-inside-avoid"
                      >
                        <ContentCard
                          item={item}
                          onLike={handleLike}
                          onClick={() => setSelectedContent(item)}
                          viewMode="masonry"
                        />
                      </motion.div>
                    </Observer>
                  ))}
                </AnimatePresence>
              </div>
            )}
            {/* Mobile-Optimized Loading Spinner */}
            {loading === "pending" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-8 sm:py-12"
              >
                <LoadingIndicator />
              </motion.div>
            )}
            {/* Mobile-Optimized No Content State */}
            {content.length === 0 && loading !== "pending" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12 sm:py-16"
              >
                <div className="text-4xl sm:text-6xl lg:text-8xl mb-4 sm:mb-6">
                  🎨
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 sm:mb-3">
                  No content found
                </h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto text-sm sm:text-base px-4">
                  Try adjusting your search terms or filters to discover amazing
                  AI-generated content
                </p>
              </motion.div>
            )}
            {/* Mobile-Optimized End of Content */}
            {!hasMore && content.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-6 sm:py-8"
              >
                <div className="inline-flex items-center space-x-2 text-gray-400">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                  <span className="text-sm sm:text-base">
                    You've seen all the amazing content!
                  </span>
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                </div>
              </motion.div>
            )}
          </Section>
        </motion.section>
        </PageLayout>
        {/* Content Modal */}
        <AnimatePresence>
          {selectedContent && (
            <ContentModal
              contentId={selectedContent.id}
              source="discover"
              onClose={() => setSelectedContent(null)}
              onLike={handleLike}
            />
          )}
        </AnimatePresence>
        <EffectDescriptionPanel
          effect={selectedEffect}
          onClose={() => setSelectedEffect(null)}
          onStartEditing={handleStartEditing}
        />
      </div>
    </>
  );
};

export default DiscoveryPage;
